import { Row, Timeline, Tooltip } from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';
import {  useSelector, useDispatch } from 'react-redux';
import Panel from '../card/Panel';
import { RootState } from '../../../store';
import { getDateWithTime } from '../../../utils/dateTimeUtils';
import { checkEmptyObj, getDefaultStr, isJson } from '../../../modules/crm/inPatient/service/crmUtils';
import { submitAdmission } from '../../../store/actions/fileAction';
import { COMMON_ERROR } from '../../constants/commonConstants';
import { centeredNotification, isSuperAdmin } from '../../../utils/utility';
import Text from 'antd/lib/typography/Text';
import { ExpandAltOutlined, InfoCircleOutlined } from '@ant-design/icons';
import TimelineDetailModal from '../timelineDetailModal/TimelineDetailModal';
import ReconciliationTimelineDetailModal from '../../../shared/components/timelineDetailModal/ReconciliationTimelineDetailModal';
import { manualStatusSync } from '../../../store/actions/claimDetailsAction';
import { ReactComponent as Sync_Icon } from '../../../assets/images/svg/Sync_icon.svg';
import { EXTENTION_KEYS } from '../../constants/crmConstants';
import FollowUpDetails from './FollowUpDetails';
import { ApplicationDetailModel } from '../../../store/models/applicationDetailModel';
import { isEmpty } from 'lodash';

interface IProps {
  timelineList: IList[];
  type?: any;
}

interface IList {
  title: string;
  remarks: any;
  date: string;
  attachments: any[];
  hisSyncStatus: any;
  isSAClaimFlowData: boolean;
  nhcxCorrolationID: any;
  flowId: any;
  statusId: any;
  insurerStatusComment?: string;
  isFollowUp: boolean;
  followUpcardTitle?: string;
  followUpcardTitleId?: string;
  followUpSubTitleId?: string;
}

interface GroupedTimelineItem extends IList {
  groupId?: string;
  itemCount?: number;
  groupItems?: IList[];
  subGroupId?: any;
  groupObj?: any;
}

const Timelines = (props: IProps): ReactElement => {
  const { timelineList, type } = props;
  const dispatch = useDispatch();
  const details: any = useSelector((state: RootState): any => state?.admissionDetails?.claimDetails);
  const {reconciliationAuditTrail, reconciliationStatus} = useSelector((state: RootState): any => state?.reconciliation);
  const payer: any = useSelector((state: RootState): any => state?.payerList?.payer);
  const isInboundError = Boolean(
    details?.payerClaimExtensionFields?.find((field: any): boolean => field?.fieldName === EXTENTION_KEYS.IS_INBOUND_ERROR)?.fieldValue);
  const [isTimelineDetailModal, setIsTimelineDetailModal] = useState(false);
  const [isRecon, setIsRecon] = useState(false);
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());
  const [processedTimelineList, setProcessedTimelineList] = useState<GroupedTimelineItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
 const {
    showClaimDetailData,
  } = useSelector((state: RootState): any => state.admissionDetails);
  const appDetail = useSelector((state: RootState): ApplicationDetailModel => state.applicationDetail);
  const isNewTpaDeskEnabled = appDetail.getIsNewTpaDeskEnabled();

  useEffect((): any => {
    if (type === 'reconciliation') {
      setIsRecon(true);
    }
  }, []);
  const onDownload = (fileIdentifier: string): any => {
    if (fileIdentifier) {
      dispatch(submitAdmission(fileIdentifier));
    } else {
      centeredNotification(COMMON_ERROR);
    }
  };

  const getDesignedRemark = (remark: any): any => {
    if (checkEmptyObj(remark)) {
      return 'Initiating...';
    } else {
      return Object.keys(remark).filter((key: any): any => (remark[key]))
        .map((key: any, index: any): any => (
          <div key={index}>
            <Text type="secondary"><strong className="fsize_sm mb-0">{key}</strong></Text>
            <p className="fsize_sm mb-0" data-testid="test-remarks">{remark[key] || '-'}</p>
          </div>
        ));
    }
  };

  const getProperRemark = (remarks: any): any => {
    if (!remarks) {
      return (
        <p className="fsize_sm mb-0" data-testid="test-remarks">{getDefaultStr(remarks, true) || '-'}</p>
      );
    } else if (typeof (remarks) === 'string') {
      if (isJson(remarks)) {
        remarks = JSON.parse(remarks);
        return getDesignedRemark(remarks);
      } else {
        return (<p className="fsize_sm mb-0" data-testid="test-remarks">{getDefaultStr(remarks, true) || '-'}</p>);
      }
    } else if (typeof remarks === 'object' && !Array.isArray(remarks)) {
      return getDesignedRemark(remarks);
    } else {
      return (
        <p className="fsize_sm mb-0" data-testid="test-remarks">{getDefaultStr(remarks, true) || '-'}</p>
      );
    }
  };

  const showModal = (): any => {
    setIsTimelineDetailModal(true);
  };

  const hideModal = (): any => {
    setIsTimelineDetailModal(false);
  };

  const statusSync = (): any => {
    const data = {
      payerClaimNumber: details?.payerClaimNo,
      hospactionId: details?.hospActionId,
      payerId: details?.payerActualId,
      statusId: details?.statusId,
      productCode: details?.productCode,
      lastUpdatedDate: details?.modifiedOn,
    };
    setIsLoading(true);
    dispatch(manualStatusSync(data, onSyncSuccess, onError));
  };

  const onSyncSuccess = (): void => {
    setIsLoading(false);
  };

  const onError = (): void => {
    setIsLoading(false);
  };

  const isMergedTimeline = (flowId: number): boolean => {
    return details?.payerClaimExtensionFields?.find((ele: any): boolean => ele?.extensionHospActionFlowId === flowId &&
    ele?.fieldName === EXTENTION_KEYS.IS_MERGED_CLAIM && ele?.fieldValue === 'true');
  };

  const isSuperAdminUser = isSuperAdmin();

  useEffect((): void => {
    if (!isSuperAdminUser && !isNewTpaDeskEnabled) {// provider's timeline without New Tpa design
      setProcessedTimelineList(timelineList);
      return;
    }
   if (isNewTpaDeskEnabled) { // only for New Tpa users we will be showing the new timeline
    const addGroupToTimelineObject = (
      groupItems: IList[],
      groupId: string,
      timeline: GroupedTimelineItem[],
    ): void => {
      if (isEmpty(groupItems)) { return; }

      let currentSubGroupItems: IList[] = [];
      let currentFollowUpId: any;
      let subGroupIndex = 0;

      const groupObj: GroupedTimelineItem = {
          ...groupItems[groupItems?.length - 1],
        groupId,
        itemCount: groupItems.length,
        groupItems,
      };

      const pushSubGroupToObj = (items: IList[], subIndex: number): void => {
        if (isEmpty(items)) { return; }
        const subGroupKey = `subGroup_${groupId}_${subIndex}`;
        const subGroupedItems = items?.map((itm: any): any => ({
          ...itm,
          groupId,
          subGroupId: subGroupKey,
          subGroupKey,
        }));

        groupObj[subGroupKey] = subGroupedItems;
      };

      groupItems?.map((item: any): void => {
        const isSameSubGroup = item?.followUpcardTitleId === currentFollowUpId;

        if (isEmpty(currentSubGroupItems) || isSameSubGroup) {
          if (isEmpty(currentSubGroupItems)) {
            currentFollowUpId = item?.followUpcardTitleId;
          }
          currentSubGroupItems.push(item);
        } else {
          pushSubGroupToObj(currentSubGroupItems, subGroupIndex++);
          currentSubGroupItems = [item];
          currentFollowUpId = item?.followUpcardTitleId;
        }
      });

      pushSubGroupToObj(currentSubGroupItems, subGroupIndex);

      timeline.push(groupObj);
    };

    const groupedTimeline: (IList | GroupedTimelineItem)[] = [];
    let currentGroupItems: IList[] = [];
    let currentGroupId = '';
    timelineList?.map((item: IList, index: number): void => {
      if (item?.followUpcardTitleId) {
        if (
          isEmpty(currentGroupItems) ||
          currentGroupItems[0].statusId === item.statusId
        ) {
          if (isEmpty(currentGroupItems)) {
            currentGroupId = `group-${index}`;
          }
          currentGroupItems.push(item);
        } else {
          addGroupToTimelineObject(currentGroupItems, currentGroupId, groupedTimeline);
          currentGroupId = `group-${index}`;
          currentGroupItems = [item];
        }
      } else {
         if (item?.isSAClaimFlowData && !isSuperAdminUser) {
           // for provider's timeline, we will not be showing this item in timeline
           return;
         }
        addGroupToTimelineObject(currentGroupItems, currentGroupId, groupedTimeline);
        currentGroupItems = [];
        groupedTimeline.push(item);
      }
    });
    addGroupToTimelineObject(currentGroupItems, currentGroupId, groupedTimeline);
    setProcessedTimelineList(groupedTimeline);
  } else { // for superAdmin users
    // Helper function to add a group of timeline items to the timeline array
    const addGroupToTimeline = (
      groupItems: IList[],
      groupId: string,
      timeline: GroupedTimelineItem[],
    ): void => {
      if (groupItems?.length) {
        timeline.push({
          ...groupItems[groupItems.length - 1], // Use the last item as base
          groupId,
          itemCount: groupItems.length,
          groupItems: [...groupItems],
        });
      }
    };

    // Process the timeline list to group related items together
    const groupedTimeline: GroupedTimelineItem[] = [];
    let currentGroupItems: IList[] = []; // Buffer for collecting items of the same group
    let currentGroupId = '';

    timelineList?.forEach((item: IList, index: number): void => {
      if (item?.isSAClaimFlowData) {
      // Group items with the same statusId together
        if (!currentGroupItems?.length || currentGroupItems?.[0]?.statusId === item?.statusId) {
          if (!currentGroupItems?.length) {
          currentGroupId = `group-${index}`;
          }
          currentGroupItems.push(item);
        } else {
          // When statusId changes, add current group and start a new one
          addGroupToTimeline(currentGroupItems, currentGroupId, groupedTimeline);
          currentGroupId = `group-${index}`;
          currentGroupItems = [item];
        }
      } else {
        // For non-SA claim flow data, add current group and add item separately
        addGroupToTimeline(currentGroupItems, currentGroupId, groupedTimeline);
        currentGroupItems = [];
        groupedTimeline.push(item);
      }
    });

    // Add any remaining grouped items
    addGroupToTimeline(currentGroupItems, currentGroupId, groupedTimeline);

    // Update the processed timeline list
    setProcessedTimelineList(groupedTimeline);
    }
  }, [timelineList, isSuperAdminUser]);

  // Toggles the expansion state of a timeline group
  // @param timelineGroupId - Unique identifier for the timeline group
  const toggleGroup = (timelineGroupId: string): void => {
    setExpandedGroupIds((currentExpandedGroups: Set<string>): Set<string> => {
      const updatedExpandedGroups = new Set(currentExpandedGroups);
      // If the group is expanded, it will be collapsed and vice versa
      if (updatedExpandedGroups?.has(timelineGroupId)) {
        updatedExpandedGroups?.delete(timelineGroupId);
      } else {
        updatedExpandedGroups?.add(timelineGroupId);
      }
      return updatedExpandedGroups;
    });
  };

  const renderTimelineItem = (item: IList, index: number, showExpandButton: boolean = false, groupId?: string): ReactElement => {
    const isSAClaimFlow = item?.isSAClaimFlowData && isSuperAdminUser;
    const isExpanded = groupId ? expandedGroupIds?.has(groupId) : false;
    const hasSubGroups = Object.keys(item).some((key: string): any => key.startsWith('subGroup_'));
    // creating a unique key with fallback for each timeline item to prevent duplication of items when grouped
    const uniqueKey = groupId ? `${groupId}-${index}` : `no-group-${index}`;

    // For grouped items, conditionally render FollowUpDetails (collapsed) or FollowUpDetails (expanded)
    if (groupId && hasSubGroups && isNewTpaDeskEnabled ) {
      return (
        <Timeline.Item key={uniqueKey} >
            <FollowUpDetails
              onClickGroup={toggleGroup}
              content={item}
              expandedGroupIds={expandedGroupIds}
              isExpanded={isExpanded}
            />
        </Timeline.Item>
      );
    }

    return (
      <Timeline.Item key={uniqueKey} className={`${isSAClaimFlow ? 'error-timeline' : ''}`}>
        <p className="mb-0 bold fsize_sm" data-testid="test-title">
          {(isSAClaimFlow && isMergedTimeline(item.flowId))
            ? 'Duplicate Merge (Manually)'
            : (isSAClaimFlow && !isInboundError)
            ? 'Incomplete Request'
            : (isSAClaimFlow && isInboundError)
            ? 'Inbound Response'
            : item.title}

          {showExpandButton && groupId && (
            <button
              className={`expand-button ${isExpanded ? 'expanded' : ''}`}
              onClick={(): void => toggleGroup(groupId)}
            >
              <span className={`${isExpanded ? 'expanded-button-text' : ''}`}>
                {isExpanded ? '-' : `+${((item as GroupedTimelineItem)?.itemCount ?? 1) - 1}`}
              </span>
            </button>
          )}

          {isSuperAdminUser && index === 0 && (
            <span>
              {(details?.isManualStatusSyncAllowed && payer?.isManualStatusSyncEnabled && !showClaimDetailData) && (
                <Tooltip title="Status Check">
                  <span className="sync_icon">
                    {!isLoading ? <Sync_Icon onClick={statusSync} /> : <span className="sync-loader"/>}
                  </span>
                </Tooltip>
              )}
            </span>
          )}
        </p>

        {(isSuperAdminUser && item?.nhcxCorrolationID) && (
          <>
            <div className="label-title fsize_sm font-italic">NHCX CorrelationID:</div>
            <span className="label-title fsize_sm">{item.nhcxCorrolationID}</span>
          </>
        )}

        {item?.hisSyncStatus != null && (
          <p className="label-title fsize_sm" data-testid="test-title">
            {item.hisSyncStatus === true ? (
              <><InfoCircleOutlined /> HIS Sync Successful</>
            ) : (
              'HIS Sync Unsuccessful'
            )}
          </p>
        )}
        {item?.insurerStatusComment &&
          <Row className="d-flex align-items-center">
            <p className="mb-0 bold fsize_sm mr-2">Insurer/TPA comments:</p>&nbsp;
            <p className="mb-0 fsize_sm">{item?.insurerStatusComment}</p>
          </Row>
        }

        {getProperRemark(item.remarks)}

        <p className="mb-0 label-title fsize_sm" data-testid="test-date">
          {getDateWithTime(item.date)}
        </p>

        {item.attachments?.map(({ fileIdentifier, fileName }: { fileIdentifier: string; fileName: string }): ReactElement => (
          <a
            key={fileIdentifier}
            className="text-elipsis d-block"
            data-testid={`${fileIdentifier}-${fileName}`}
            href="#!"
            onClick={(): void => onDownload(fileIdentifier)}
          >
            Download
          </a>
        ))}
      </Timeline.Item>
    );
  };

  return (
    <Panel
      title="Timeline"
      className="timeline-scroll"
      extra={
        details?.claimFlowData || (reconciliationAuditTrail?.length > 0 && !['Fully Paid', 'Fully Paid (Bulk)']
        .includes(reconciliationStatus)) ?
          <Tooltip title="Timeline Details">
            <span onClick={showModal} className="mouse-pointer" data-testid="timeline-detail-button">
              <ExpandAltOutlined />
            </span>
          </Tooltip> : ''
      }
    >
      <Timeline>
        {processedTimelineList?.map((item: GroupedTimelineItem, index: number): any => {
          if (item?.groupId && item?.groupItems && isNewTpaDeskEnabled) {
            return (
                renderTimelineItem(
                  item,
                  index,
                  true, // Show expand button for new tpa users
                  item.groupId,
                )
            );
          } else if (item?.groupItems && item?.groupId && expandedGroupIds?.has(item?.groupId)) {
            return item?.groupItems.map((groupItem: IList, groupIndex: number): ReactElement =>
              renderTimelineItem(groupItem, groupIndex, groupIndex === 0, item.groupId),
            );
          } else {
          return renderTimelineItem(item, index, Boolean((item?.groupItems || []).length > 1), item?.groupId);
          }
        })}
      </Timeline>

      {isRecon ? (
        <ReconciliationTimelineDetailModal
          isTimelineDetailModal={isTimelineDetailModal}
          handleCancel={hideModal}
        />
      ) : (
        <TimelineDetailModal
          isTimelineDetailModal={isTimelineDetailModal}
          handleCancel={hideModal}
        />
      )}
    </Panel>
  );
};

export default Timelines;
